import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import { useRange, withRange } from '../../range';
import { Band, Card } from '../../components/Card';
import DataTable from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import KpiTile from '../../components/KpiTile';
import LineTrend from '../../components/LineTrend';
import PageHeader from '../../components/PageHeader';
import { formatCompactNumber, formatMs, formatPct } from '../../format';
import useSort, { applySort } from '../../hooks/useSort';

/**
 * AI › Try-on — pipeline health.
 *
 * The substance here was already right and is preserved: percentiles rather
 * than a mean (a mean hides the tail that generates the complaints), the
 * worst-day caveat stated on the tiles rather than buried, failure reasons
 * broken out by garment type, and segments suppressed below the volume where
 * a percentile means anything.
 *
 * Two things changed. The page-local 7/30/90 selector is gone — the range now
 * comes from the shell, so this page and every other one answer for the same
 * window. And the "not shown here yet" note about try-on's effect on buying
 * now links to Funnels, which exists.
 */
const RAMP = { p50: '#86b6ef', p90: '#2a78d6', p99: '#104281' };

function LatencyTiles({ latency }) {
  const cells = [
    { key: 'p50_ms', label: 'p50 — typical', color: RAMP.p50 },
    { key: 'p90_ms', label: 'p90', color: RAMP.p90 },
    { key: 'p99_ms', label: 'p99 — worst tail', color: RAMP.p99 },
  ];
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {cells.map((c) => (
        <div key={c.key} className="relative overflow-hidden rounded-xl border border-hairline bg-surface px-4 py-3.5 shadow-sm">
          <span className="absolute left-0 top-0 h-1 w-full" style={{ background: c.color }} aria-hidden="true" />
          <div className="text-sm font-medium text-ink-secondary">{c.label}</div>
          <div className="mt-1 text-2xl font-semibold tracking-tight text-ink">{formatMs(latency?.[c.key])}</div>
        </div>
      ))}
    </div>
  );
}

function SegmentTable({ title, rows, minVolume }) {
  // Hooks must run unconditionally on every render — see the Rules of Hooks
  // — so they come before the empty-rows early return below, not after it.
  const columns = [
    { key: 'segment', label: 'Segment', sortable: true, render: (r) => <span className="font-medium text-ink">{r.segment}</span> },
    { key: 'requested', label: 'Run', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.requested) },
    { key: 'completed', label: 'Completed', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.completed) },
    { key: 'failed', label: 'Failed', align: 'right', render: (r) => formatCompactNumber(r.failed) },
    { key: 'success_rate_pct', label: 'Success', align: 'right', sortable: true, render: (r) => formatPct(r.success_rate_pct, 1) },
    { key: 'p50_ms', label: 'p50', align: 'right', sortable: true, render: (r) => formatMs(r.p50_ms) },
    { key: 'p90_ms', label: 'p90', align: 'right', sortable: true, render: (r) => formatMs(r.p90_ms) },
    { key: 'p99_ms', label: 'p99', align: 'right', render: (r) => formatMs(r.p99_ms) },
  ];
  const { sort, onSort } = useSort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sortedRows = useMemo(() => applySort(rows, sort, columns), [rows, sort]);

  if (!rows || rows.length === 0) return null;

  return (
    <Band
      title={title}
      actions={
        <span className="text-[11px] text-ink-muted">
          Percentiles need {minVolume}+ completed try-ons in a day; thinner segments show counts only.
        </span>
      }
    >
      <DataTable columns={columns} rows={sortedRows} rowKey={(r) => r.segment} dense sort={sort} onSort={onSort} />
    </Band>
  );
}

export default function TryOnPage() {
  const range = useRange();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get('/tryon/overview', { params: { days: range.days } });
      setData(data);
    } catch (err) {
      toast.error(errMsg(err, 'Could not load try-on metrics'));
    } finally {
      setLoading(false);
    }
  }, [range.days]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const trend = data?.trend || [];
  const noData = data?.status === 'no_data';

  const latencySeries = ['p50', 'p90', 'p99'].map((p) => ({
    key: p, label: p, color: RAMP[p],
    values: trend.map((d) => ({ x: d.date, y: d[`${p}_ms`] })),
  }));
  const outcomeSeries = [{
    key: 'ok', label: 'Success rate', color: '#2a78d6',
    values: trend.map((d) => ({ x: d.date, y: d.success_rate_pct })),
  }];

  const failureColumns = [
    { key: 'reason', label: 'Reason', sortable: true, render: (r) => <span className="font-mono text-[11px] text-ink">{r.reason}</span> },
    { key: 'count', label: 'Failures', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.count) },
    { key: 'share_of_failures_pct', label: 'Share', align: 'right', sortable: true, render: (r) => formatPct(r.share_of_failures_pct, 1) },
    {
      key: 'worst',
      label: 'Worst garment types',
      render: (r) => (data?.failure_reasons_by_category || [])
        .filter((c) => c.reason === r.reason)
        .slice(0, 3)
        .map((c) => `${c.category} (${c.count})`)
        .join(', ') || null,
    },
  ];

  const { sort: failSort, onSort: onFailSort } = useSort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sortedFailures = useMemo(() => applySort(data?.failure_reasons || [], failSort, failureColumns), [data, failSort]);

  return (
    <div>
      <PageHeader
        title="Try-on"
        hint={`Pipeline health — ${range.label.toLowerCase()}. Success rate, latency tails, and why failures fail.`}
      />

      {loading && !data && <Card><p className="text-sm text-ink-muted">Loading…</p></Card>}

      {noData && (
        <EmptyState
          tone="gap"
          title="No try-on rollup for this window"
          detail="These numbers come from the nightly rollup of tryon.requested / completed / failed. Either the events were instrumented after this window, or the nightly job has not run for it. This is an empty state, not a zero."
        />
      )}

      {data && !noData && (
        <>
          <Band title="Outcomes">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiTile label="Try-ons run" value={formatCompactNumber(totals.requested)}
                       caption="Cache hits excluded — they run no pipeline." />
              <KpiTile label="Completed" value={formatCompactNumber(totals.completed)} />
              <KpiTile label="Failed" value={formatCompactNumber(totals.failed)} goodWhen="down" />
              <KpiTile label="Success rate" value={formatPct(totals.success_rate_pct, 1)}
                       caption="Of jobs that finished in this window." />
            </div>
            <div className="mt-4">
              <LineTrend series={outcomeSeries} unit="%" yMax={100} format={(v) => Math.round(v)} height={140} />
            </div>
          </Band>

          <Band
            title="Latency — p50 / p90 / p99"
            actions={
              <span className="text-[11px] text-ink-muted">
                Tiles show the worst single day in the window; the trend shows each day.
              </span>
            }
          >
            <LatencyTiles latency={data.latency} />
            <div className="mt-4">
              <LineTrend series={latencySeries} format={(v) => (v / 1000).toFixed(1)} unit="s" height={200} />
            </div>
            <p className="mt-2 text-[11px] leading-snug text-ink-muted">
              A true range-wide percentile cannot be recovered from daily rollups, so the tiles show the
              worst day rather than implying something they are not.
            </p>
          </Band>

          <Band title="Why try-ons failed">
            <DataTable
              columns={failureColumns}
              rows={sortedFailures}
              rowKey={(r) => r.reason}
              sort={failSort}
              onSort={onFailSort}
              empty={<EmptyState title="No failures in this window" />}
            />
          </Band>

          <SegmentTable title="By brand" rows={data.by_brand} minVolume={data.min_segment_volume} />
          <SegmentTable title="By garment type" rows={data.by_category} minVolume={data.min_segment_volume} />

          <Band title="Not answered here">
            <div className="space-y-3">
              <div className="rounded-xl border border-dashed border-baseline bg-surface px-4 py-3">
                <div className="text-sm font-medium text-ink">Does a try-on change what people buy?</div>
                <p className="mt-1 text-xs leading-relaxed text-ink-secondary">
                  That is a funnel question, not a pipeline one — approximating it from two daily totals
                  would produce a number nobody could defend.{' '}
                  <Link to={withRange('/growth/funnel', range)} className="font-medium text-raspberry-600 hover:text-raspberry-700">
                    Open Funnel →
                  </Link>
                </p>
              </div>
              <div className="rounded-xl border border-dashed border-baseline bg-surface px-4 py-3">
                <div className="text-sm font-medium text-ink">Size accuracy against self-reported fit</div>
                <p className="mt-1 text-xs leading-relaxed text-ink-secondary">
                  Blocked upstream: it needs a <code className="text-[11px]">feedback.submitted</code> event, and how
                  feedback gets collected is still an open product decision. No proxy is offered in its place.
                </p>
              </div>
            </div>
          </Band>
        </>
      )}
    </div>
  );
}
